import { getAuthHeaders } from './authActions'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const parseResponse = async (res) => {
	const text = await res.text()
	if (!text) return null
	try {
		return JSON.parse(text)
	} catch {
		return text
	}
}

const fetchWithAuth = async (endpoint, options = {}) => {
	const { method = 'GET', headers = {}, ...rest } = options
	const res = await fetch(`${BASE_URL}${endpoint}`, {
		method,
		headers: {
			...getAuthHeaders(),
			...headers,
		},
		...rest,
	})

	const payload = await parseResponse(res)
	if (!res.ok) {
		const message =
			typeof payload === 'string'
				? payload
				: payload?.message || 'Request failed'
		const error = new Error(message || 'Request failed')
		error.status = res.status
		error.code = payload?.code
		throw error
	}

	return payload
}

export const getOrgUsers = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/users`)
}

export const getOrgMyRole = (orgSlug) =>
	fetchWithAuth(`/api/${orgSlug}/org/myrole`)
export const getOrgMyPositions = (orgSlug) =>
	fetchWithAuth(`/api/${orgSlug}/org/mypositions`)

export const getOrgExperiences = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/experiences`)
}

export const getOrgLiveStatus = (orgSlug, { signal } = {}) =>
	fetchWithAuth(`/api/${orgSlug}/org/live-status`, { signal })

export const searchOrg = (orgSlug, { query, type = 'all', page = 1, limit = 5, signal } = {}) => {
	const params = new URLSearchParams({ q: query, type, page: String(page), limit: String(limit) })
	return fetchWithAuth(`/api/${orgSlug}/org/search?${params}`, { signal })
}

export const getOrgLocations = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/locations`)
}

export const createOrgLocation = (orgSlug, name) => {
	return fetchWithAuth(`/api/${orgSlug}/org/locations`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ name }),
	})
}

export const updateOrgLocation = (orgSlug, locationId, name) => {
	return fetchWithAuth(`/api/${orgSlug}/org/locations/${locationId}`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ name }),
	})
}

export const deleteOrgLocation = (orgSlug, locationId) => {
	return fetchWithAuth(`/api/${orgSlug}/org/locations/${locationId}`, {
		method: 'DELETE',
	})
}

export const getOrgDetails = (orgSlug) =>
	fetchWithAuth(`/api/${orgSlug}/org/details`)

export const getOrgBillingDashboard = (orgSlug, { year, month } = {}) => {
	const params = new URLSearchParams()
	if (year !== undefined && year !== null) params.set('year', String(year))
	if (month !== undefined && month !== null) params.set('month', String(month))
	const suffix = params.toString() ? `?${params.toString()}` : ''
	return fetchWithAuth(`/api/${orgSlug}/org/billing/dashboard${suffix}`)
}

export const createOrgStripeSetupSession = (orgSlug, payload = {}) => {
	return fetchWithAuth(`/api/${orgSlug}/org/billing/stripe/setup-session`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload || {}),
	})
}

export const syncOrgStripeSetupSession = (orgSlug, sessionId) => {
	return fetchWithAuth(`/api/${orgSlug}/org/billing/stripe/sync-session`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ sessionId }),
	})
}

export const createOrgStripePortalSession = (orgSlug, payload = {}) => {
	return fetchWithAuth(`/api/${orgSlug}/org/billing/stripe/portal-session`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload || {}),
	})
}

export const getOrgBillingAlerts = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/billing/alerts`)
}

export const getOrgCommerceSettings = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/commerce/settings`)
}

export const updateOrgCommerceSettings = (orgSlug, payload = {}) => {
	return fetchWithAuth(`/api/${orgSlug}/org/commerce/settings`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload || {}),
	})
}

export const createOrgCommerceStripeOnboardingLink = (
	orgSlug,
	payload = {},
) => {
	return fetchWithAuth(`/api/${orgSlug}/org/commerce/stripe/onboarding-link`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload || {}),
	})
}

export const refreshOrgCommerceStripeStatus = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/commerce/stripe/refresh`, {
		method: 'POST',
	})
}

export const createOrgCommerceStripeDashboardLink = (orgSlug) => {
	return fetchWithAuth(`/api/${orgSlug}/org/commerce/stripe/dashboard-link`, {
		method: 'POST',
	})
}
